import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { v1 as uuidv1, v4 as uuidv4, NIL as NIL_UUID } from "uuid";

import { Container, ContentContainer } from "@src/core/styles";
import { useSeo } from "@src/core/hooks";
import { APP_ROUTES } from "@src/core/constants";
import {
  ActionButton,
  ArticleBody,
  Chip,
  InfoGrid,
  InfoItem,
  NumberInput,
  Panel,
  PanelActions,
  PanelBody,
  PanelHeader,
  PanelTitle,
  Section,
  SectionTitle,
  SmallButton,
  Tabs,
  ToolContainer,
  ToolIntro,
  ToolLabel,
  ToolSubtitle,
  ToolTitle,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
} from "@src/pages/dev-tools-shared";
import {
  UuidList,
  UuidRow,
  UuidValue,
} from "./uuid-generator-page.styles";

type UuidVersion = "v1" | "v4" | "v7" | "nil";

const VERSIONS: { id: UuidVersion; label: string; hint: string }[] = [
  {
    id: "v4",
    label: "UUID v4",
    hint: "Случайный. Самый популярный, 122 бита энтропии.",
  },
  {
    id: "v7",
    label: "UUID v7",
    hint: "Временной префикс + случайность. Сортируется по времени создания.",
  },
  {
    id: "v1",
    label: "UUID v1",
    hint: "На основе MAC‑адреса и времени. Устаревший.",
  },
  {
    id: "nil",
    label: "Nil UUID",
    hint: "00000000-0000-0000-0000-000000000000.",
  },
];

const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * UUID v7 (RFC 9562) — 48-bit unix ms timestamp + 74 random bits + version/variant bits.
 * Implemented without BigInt so it works with ES<2020 target.
 */
const uuidv7 = (): string => {
  const bytes = new Uint8Array(16);
  const ms = Date.now();

  // Date.now() fits in 53 bits; top 16 bits occupy ~[0..65535] range.
  const high = Math.floor(ms / 0x100000000);
  const low = ms >>> 0;

  bytes[0] = (high >>> 8) & 0xff;
  bytes[1] = high & 0xff;
  bytes[2] = (low >>> 24) & 0xff;
  bytes[3] = (low >>> 16) & 0xff;
  bytes[4] = (low >>> 8) & 0xff;
  bytes[5] = low & 0xff;

  const randomTail = new Uint8Array(10);
  crypto.getRandomValues(randomTail);
  bytes.set(randomTail, 6);

  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex: string[] = [];
  for (let i = 0; i < 16; i += 1) {
    hex.push(bytes[i].toString(16).padStart(2, "0"));
  }
  return (
    `${hex.slice(0, 4).join("")}-` +
    `${hex.slice(4, 6).join("")}-` +
    `${hex.slice(6, 8).join("")}-` +
    `${hex.slice(8, 10).join("")}-` +
    `${hex.slice(10, 16).join("")}`
  );
};

const generateOne = (version: UuidVersion): string => {
  switch (version) {
    case "v1":
      return uuidv1();
    case "v4":
      return uuidv4();
    case "v7":
      return uuidv7();
    case "nil":
      return NIL_UUID;
    default:
      return uuidv4();
  }
};

export const UuidGeneratorPage: FC = () => {
  const [version, setVersion] = useState<UuidVersion>("v4");
  const [count, setCount] = useState<number>(5);
  const [uppercase, setUppercase] = useState<boolean>(false);
  const [withoutDashes, setWithoutDashes] = useState<boolean>(false);
  const [uuids, setUuids] = useState<string[]>(() => [uuidv4()]);

  const [validateInput, setValidateInput] = useState<string>("");
  const validationState: "idle" | "valid" | "invalid" = useMemo(() => {
    const trimmed = validateInput.trim();
    if (!trimmed) return "idle";
    return uuidRegex.test(trimmed) ? "valid" : "invalid";
  }, [validateInput]);

  const detectedVersion = useMemo(() => {
    const trimmed = validateInput.trim();
    if (validationState !== "valid") return null;
    if (trimmed === NIL_UUID) return "Nil";
    const v = trimmed[14];
    return `v${v}`;
  }, [validateInput, validationState]);

  const formatValue = useCallback(
    (value: string): string => {
      let result = value;
      if (withoutDashes) result = result.replace(/-/g, "");
      if (uppercase) result = result.toUpperCase();
      return result;
    },
    [uppercase, withoutDashes]
  );

  const handleGenerate = () => {
    const safeCount = Math.max(1, Math.min(1000, Math.floor(count) || 1));
    const next = Array.from({ length: safeCount }, () => generateOne(version));
    setUuids(next);
  };

  const copyOne = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar("UUID скопирован", { variant: "success", preventDuplicate: true });
    } catch {
      enqueueSnackbar("Не удалось скопировать", { variant: "error" });
    }
  };

  const copyAll = async () => {
    if (!uuids.length) return;
    const text = uuids.map(formatValue).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar(`Скопировано ${uuids.length} UUID`, {
        variant: "success",
        preventDuplicate: true,
      });
    } catch {
      enqueueSnackbar("Не удалось скопировать", { variant: "error" });
    }
  };

  const downloadTxt = () => {
    const text = uuids.map(formatValue).join("\n");
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `uuids-${version}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "UUID Generator — генератор UUID v1/v4/v7 онлайн",
      applicationCategory: "DeveloperApplication",
      operatingSystem: "All",
      offers: { "@type": "Offer", price: "0", priceCurrency: "RUB" },
      description:
        "Бесплатный онлайн-генератор UUID: версии v1, v4, v7 и Nil. Массовая генерация, валидация и копирование в буфер.",
      url: `${APP_ROUTES.UUID_GENERATOR}`,
    }),
    []
  );

  useSeo({
    title: "UUID Generator — сгенерировать UUID v1 / v4 / v7 онлайн",
    description:
      "Бесплатный онлайн генератор UUID версий v1, v4 и v7. Массовая генерация до 1000 идентификаторов, копирование, экспорт в файл и валидация.",
    canonical: APP_ROUTES.UUID_GENERATOR,
    jsonLd,
  });

  return (
    <Container>
      <ContentContainer>
        <ToolContainer>
          <ToolIntro>
            <ToolTitle>UUID Generator</ToolTitle>
            <ToolSubtitle>
              Сгенерируйте UUID v1, v4 или v7, проверьте корректность
              существующего идентификатора. Всё работает локально в браузере —
              значения не отправляются на сервер.
            </ToolSubtitle>
          </ToolIntro>

          <Tabs>
            {VERSIONS.map((v) => (
              <Chip
                key={v.id}
                $active={version === v.id}
                onClick={() => setVersion(v.id)}
                title={v.hint}
                type="button"
              >
                {v.label}
              </Chip>
            ))}
          </Tabs>

          <Toolbar>
            <ToolbarLeft>
              <ActionButton variant="contained" onClick={handleGenerate}>
                Сгенерировать
              </ActionButton>
              <ToolLabel>Количество:</ToolLabel>
              <NumberInput
                type="number"
                min={1}
                max={1000}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
              />
            </ToolbarLeft>
            <ToolbarRight>
              <Chip
                $active={uppercase}
                onClick={() => setUppercase((v) => !v)}
                type="button"
              >
                UPPERCASE
              </Chip>
              <Chip
                $active={withoutDashes}
                onClick={() => setWithoutDashes((v) => !v)}
                type="button"
              >
                Без дефисов
              </Chip>
            </ToolbarRight>
          </Toolbar>

          <Panel>
            <PanelHeader>
              <PanelTitle>Результат ({uuids.length})</PanelTitle>
              <PanelActions>
                <SmallButton variant="outlined" onClick={copyAll}>
                  Копировать все
                </SmallButton>
                <SmallButton variant="outlined" onClick={downloadTxt}>
                  Скачать .txt
                </SmallButton>
              </PanelActions>
            </PanelHeader>
            <UuidList>
              {uuids.map((raw, idx) => {
                const value = formatValue(raw);
                return (
                  <UuidRow key={`${raw}-${idx}`}>
                    <UuidValue>{value}</UuidValue>
                    <SmallButton
                      variant="outlined"
                      onClick={() => copyOne(value)}
                    >
                      Копировать
                    </SmallButton>
                  </UuidRow>
                );
              })}
            </UuidList>
          </Panel>

          <InfoGrid>
            <InfoItem>
              <strong>Версия:</strong> {version.toUpperCase()}
            </InfoItem>
            <InfoItem>
              <strong>Длина:</strong> {withoutDashes ? 32 : 36} символа
            </InfoItem>
            <InfoItem>
              <strong>Энтропия v4:</strong> 122 бита
            </InfoItem>
          </InfoGrid>

          <Section>
            <SectionTitle>Валидация UUID</SectionTitle>
            <Panel>
              <PanelBody $padding>
                <input
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid #C2C9BD",
                    borderRadius: 8,
                    fontFamily:
                      "JetBrains Mono, Fira Code, Courier New, monospace",
                    fontSize: 13,
                  }}
                  placeholder="Вставьте UUID для проверки: 550e8400-e29b-41d4-a716-446655440000"
                  value={validateInput}
                  onChange={(e) => setValidateInput(e.target.value)}
                />
                {validationState === "valid" && (
                  <InfoItem>
                    <strong>Валидный UUID.</strong>
                    {detectedVersion ? ` Обнаружена версия ${detectedVersion}.` : ""}
                  </InfoItem>
                )}
                {validationState === "invalid" && (
                  <InfoItem>
                    <strong>Невалидный UUID.</strong> Проверьте формат
                    (8‑4‑4‑4‑12, hex, корректные версия и variant bits).
                  </InfoItem>
                )}
              </PanelBody>
            </Panel>
          </Section>

          <Section>
            <SectionTitle>Какой UUID выбрать?</SectionTitle>
            <ArticleBody>
              <p>
                <strong>UUID v4</strong> — полностью случайный, самый популярный
                в CRUD‑приложениях, идентификаторах сущностей и API‑ключах.
                Используйте по умолчанию, если нет специальных требований.
              </p>
              <p>
                <strong>UUID v7</strong> — современный формат (RFC 9562): первые
                48 бит это Unix‑миллисекунды, дальше — случайность. Благодаря
                времени такие UUID <strong>сортируются по порядку создания</strong>{" "}
                и хорошо ложатся в B‑tree индексы БД (PostgreSQL, MySQL).
                Рекомендуется для новых проектов вместо v4 как primary key.
              </p>
              <p>
                <strong>UUID v1</strong> — на основе MAC‑адреса и времени.
                Сегодня почти не используется: раскрывает адрес сетевой карты и
                уступает v7 по всем метрикам.
              </p>
              <p>
                <strong>Nil UUID</strong> — нулевой идентификатор{" "}
                <code>00000000-0000-0000-0000-000000000000</code>. Обычно
                используется как «пустое» значение.
              </p>
              <h3>Хочется проверять ответы с UUID в тестах?</h3>
              <p>
                В LightBoxAPI можно сделать мок‑маршрут, который возвращает новый UUID
                на каждый запрос — удобно тестировать идемпотентность и
                десериализацию ID на фронте. Создайте мок‑API за одну минуту{" "}
                в <a href={APP_ROUTES.DASHBOARD}>панели управления</a>.
              </p>
            </ArticleBody>
          </Section>
        </ToolContainer>
      </ContentContainer>
    </Container>
  );
};
